<template>
  <div v-if="password" class="mt-2">
    <div class="flex items-center justify-between mb-2">
      <span class="text-sm font-medium text-gray-700">Password strength</span>
      <span :class="strengthTextClass" class="text-sm font-medium">
        {{ strengthText }}
      </span>
    </div>

    <div class="w-full bg-gray-200 rounded-full h-2 mb-3">
      <div
        :class="strengthBarClass"
        :style="{ width: strengthPercentage + '%' }"
        class="h-2 rounded-full transition-all duration-300"
      ></div>
    </div>

    <div v-if="showRequirements" class="space-y-1">
      <div
        v-for="requirement in requirements"
        :key="requirement.id"
        class="flex items-center text-sm"
      >
        <svg
          :class="requirement.met ? 'text-green-500' : 'text-gray-400'"
          class="h-4 w-4 mr-2 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            v-if="requirement.met"
            fill-rule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clip-rule="evenodd"
          />
          <circle v-else cx="10" cy="10" r="3" />
        </svg>
        <span :class="requirement.met ? 'text-green-700' : 'text-gray-600'">
          {{ requirement.text }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  interface Props {
    password: string;
    showRequirements?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    showRequirements: true,
  });

  interface Requirement {
    id: string;
    text: string;
    met: boolean;
    weight: number;
  }

  const requirements = computed((): Requirement[] => [
    {
      id: 'length',
      text: 'At least 8 characters',
      met: props.password.length >= 8,
      weight: 25,
    },
    {
      id: 'lowercase',
      text: 'One lowercase letter',
      met: /[a-z]/.test(props.password),
      weight: 20,
    },
    {
      id: 'uppercase',
      text: 'One uppercase letter',
      met: /[A-Z]/.test(props.password),
      weight: 20,
    },
    {
      id: 'number',
      text: 'One number',
      met: /\d/.test(props.password),
      weight: 20,
    },
    {
      id: 'special',
      text: 'One special character',
      met: /[!@#$%^&*(),.?":{}|<>]/.test(props.password),
      weight: 15,
    },
  ]);

  const strengthScore = computed((): number => {
    return requirements.value.reduce((score, req) => {
      return score + (req.met ? req.weight : 0);
    }, 0);
  });

  const strengthLevel = computed((): 'weak' | 'fair' | 'good' | 'strong' => {
    const score = strengthScore.value;
    if (score < 40) return 'weak';
    if (score < 65) return 'fair';
    if (score < 85) return 'good';
    return 'strong';
  });

  const strengthText = computed((): string => {
    const level = strengthLevel.value;
    return level.charAt(0).toUpperCase() + level.slice(1);
  });

  const strengthPercentage = computed((): number => {
    return Math.min(strengthScore.value, 100);
  });

  const strengthTextClass = computed((): string => {
    const level = strengthLevel.value;
    const classes = {
      weak: 'text-red-600',
      fair: 'text-yellow-600',
      good: 'text-blue-600',
      strong: 'text-green-600',
    };
    return classes[level];
  });

  const strengthBarClass = computed((): string => {
    const level = strengthLevel.value;
    const classes = {
      weak: 'bg-red-500',
      fair: 'bg-yellow-500',
      good: 'bg-blue-500',
      strong: 'bg-green-500',
    };
    return classes[level];
  });
</script>
